import React, { createContext, useContext, useEffect, useState } from "react";
import helpers from "../../lib/crypto";
import Joi from "joi";
import axios from "axios";

const FunctionContext = createContext();
const API_URL = import.meta.env.VITE_API_URL;

const FunctionalContext = ({ children }) => {
  // Posts from admin_get/postings
  const [posts, setPosts] = useState([]);
  const [postSkip, setPostSkip] = useState(0);
  const [postLimit] = useState(3);
  const [postLoading, setPostLoading] = useState(false);
  const [postHasMore, setPostHasMore] = useState(true);
  const [postErrors, setPostErrors] = useState(null);

  // Listings from crm/listings
  const [listings, setListings] = useState([]);
  const [listSkip, setListSkip] = useState(0);
  const [listLimit] = useState(3);
  const [listLoading, setListLoading] = useState(false);
  const [listHasMore, setListHasMore] = useState(true);
  const [listErrors, setListErrors] = useState(null);

  //  live id
  const ORG_ID = "O088E2B987E0C49";


  //staging id
  // const ORG_ID = "OB0788A47F46044";

  const get_listings = (data) => {
    const schema = Joi.object({
      skip: Joi.number().required(),
      limit: Joi.number().required(),
      organisation_id: Joi.string().required(),
      key: Joi.string().required(),
      listing_id: Joi.string().optional().allow("", null),
    });
    return schema.validate(data);
  };

  // fetchPosts - for admin_get/postings
  const fetchPosts = async (curSkip = 0, append = false) => {
    setPostLoading(true);
    try {
      const payload = {
        skip: curSkip,
        limit: postLimit,
        organisation_id: ORG_ID,
        key: "crm",
      };

      const encrypted = helpers.encryptobj(payload);

      const res = await axios.post(
        `${API_URL}/admin_get/postings`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      // console.log(res);

      const list = res?.data?.postings || [];
      const total = res?.data?.count || 0;

      // console.log(list, "posts");

      if (curSkip === 0) {
        setPosts(list);
      } else {
        setPosts((prev) => (append ? [...prev, ...list] : list));
      }

      setPostHasMore(curSkip + list.length < total);
      setPostSkip(curSkip + list.length);

      // console.log(postHasMore);
      // console.log(postSkip);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setPostErrors(err?.message || "Failed to load posts");
      setPostHasMore(false);
    } finally {
      setPostLoading(false);
    }
  };

  // fetchListings - for crm/listings
  const fetchListings = async (curSkip = 0, append = false) => {
    setListLoading(true);
    setListErrors(null);

    const payload = {
      skip: curSkip,
      limit: 999999,
      organisation_id: ORG_ID,
      key: "crm",
    };

    // console.log(payload)

    const { error } = get_listings(payload);
    if (error) {
      setListErrors("Invalid payload");
      setListLoading(false);
      return;
    }

    try {
      const encrypted = helpers.encryptobj(payload);

      const response = await axios.post(
        `${API_URL}/crm/listings`,
        { enc: encrypted },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // console.log(response);

      const list = response?.data?.listings || [];
      const total = response?.data?.count || 0;
      // console.log(list, "listings");

      if (curSkip === 0) {
        setListings(list);
      } else {
        setListings((prev) => (append ? [...prev, ...list] : list));
      }
      setListHasMore(curSkip + list.length < total);
      setListSkip(curSkip + list.length);
    } catch (err) {
      console.error("Error fetching listings:", err);
      setListErrors("Failed to load listings");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchListings();
  }, []);

  const value = React.useMemo(() => ({
    // Posts
    posts,
    postLoading,
    postHasMore,
    postErrors,
    fetchPosts,

    // Listings
    listings,
    listLoading,
    listHasMore,
    listErrors,
    fetchListings,

    // id
    ORG_ID,
  }), [
    posts,
    postLoading,
    postHasMore,
    postErrors,
    listings,
    listLoading,
    listHasMore,
    listErrors
  ]);

  return (
    <FunctionContext.Provider value={value}>
      {children}
    </FunctionContext.Provider>
  );
};

export default FunctionalContext;

export const useFunctionContext = () => {
  const context = useContext(FunctionContext);
  if (!context) {
    throw new Error("useFunctionContext must be used within FunctionalContext");
  }
  return context;
};
